import { defineComponent as _defineComponent } from 'vue'
import { useCampaignsStore } from '@/stores/campaigns-store';
import Checkbox from '@sh/components/Utils/Checkbox.ts.vue';
import DropdownTabContent from '@sh/components/Utils/DropdownTabContent.ts.vue';
import FilterDropdown from '@sh/components/Utils/FilterDropdown.ts.vue';
import OptimizerIcon from '@sh/components/Utils/OptimizerIcon.ts.vue';
import { FilterDto } from '@sh/types';
import { BDropdown } from 'bootstrap-vue';
import { cloneDeep, every, filter, isEmpty, size } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, Ref, ref } from 'vue';

interface Props {
  statusOptions: FilterDto[];
  tagsLevel: 'campaign' | 'content';
  tsType: string;
  showManagedStatusTab?: boolean;
}

interface Emits {
  (e: 'on-filter-changed'): void;
}

type BDropdownInstance = InstanceType<typeof BDropdown>;


export default /*#__PURE__*/_defineComponent({
  __name: 'CampaignsFilters.ts',
  props: {
    statusOptions: null,
    tagsLevel: null,
    tsType: null,
    showManagedStatusTab: { type: Boolean }
  },
  emits: ["on-filter-changed"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'on-filter-changed'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  statusOptions: FilterDto[];
  tagsLevel: 'campaign' | 'content';
  tsType: string;
  showManagedStatusTab?: boolean;
};





const campaignFiltersRef: Ref<BDropdownInstance | null> = ref(null);

const searchTag = ref('');
const searchStatus = ref('');
const searchCampaigns = ref('');
const defaultManagedStatus = ref<string | null>('MANAGED');
const defaultArchivedStatus = ref<string | null>('UNARCHIVED');

const campaignsStore = useCampaignsStore();
const { filterState } = campaignsStore;
const { getAllTags } = storeToRefs(campaignsStore);

const appliedFiltersLength = computed<number>(() => {
  const filtered = filter(filterState.savedFilters, (item) => !isEmpty(item));
  return size(filtered);
});

const filteredTagValues = computed<string[]>(() => {
  return getAllTags.value.filter((tag: string) =>
    searchTag ? tag.toLowerCase().includes(searchTag.value.toLowerCase()) : tag
  );
});

const isFilterStateEmpty = computed<boolean>(() => every(filterState.filterSession, isEmpty));

function handleTagSelection(tag: string) {
  const index = filterState.filterSession.tags.indexOf(tag);
  if (index === -1) {
    filterState.filterSession.tags.push(tag);
  } else {
    filterState.filterSession.tags.splice(index, 1);
  }
}

const isTagSelected = (tag: string) => filterState.filterSession.tags.includes(tag);

const statusValues = computed<FilterDto[]>(() => {
  return props.statusOptions
    .filter((option) => option.value.status !== 'ARCHIVED' && option.value.status !== 'UNMANAGED')
    .filter((item) =>
      searchStatus.value ? item.value.status.toLowerCase().includes(searchStatus.value.toLowerCase()) : true
    );
});

const isTagsSearchBarShown = computed<boolean>(() => filteredTagValues.value.length > 0 || Boolean(searchTag));
const isStatusSearchBarShown = computed<boolean>(() => statusValues.value.length > 0 || Boolean(searchStatus));

function handleAllValueSelected(tab: string, checked: boolean) {
  if (tab === 'status') {
    if (checked) {
      filterState.filterSession.status = cloneDeep(statusValues.value);
    } else {
      filterState.filterSession.status = [];
    }
  }
  if (tab === 'managed') {
    filterState.filterSession.managed = [];
    defaultManagedStatus.value = null;
  }
  if (tab === 'archived') {
    filterState.filterSession.archived = [];
    defaultArchivedStatus.value = null;
  }
  if (tab === 'tags') {
    filterState.filterSession.tags = [];
  }
}

const statusSelection = computed(() => {
  return {
    indeterminate:
      (filterState.filterSession.status.length &&
        filterState.filterSession.status.length < statusValues.value.length) ||
      false,
    allAccountsSelected: filterState.filterSession.status.length === statusValues.value.length,
  };
});

function handleStatusSelection(value: FilterDto, checked: boolean, tab: string) {
  if (tab === 'status') {
    if (checked) {
      filterState.filterSession.status.push(value);
    } else {
      const index = filterState.filterSession.status.findIndex((el) => el.content === value.content);
      if (index !== -1) filterState.filterSession.status.splice(index, 1);
    }
  }
}

function manageStatusSelection(value: string, tab: string) {
  if (tab === 'managed') {
    filterState.filterSession.managed = [{ value: { status: value }, content: value, group: 'status' }];
  } else if (tab === 'archived') {
    filterState.filterSession.archived = [{ value: { status: value }, content: value, group: 'status' }];
  }
}

function handleOnFilterValues() {
  emit('on-filter-changed');
  filterState.savedFilters = cloneDeep(filterState.filterSession);
  const dropdownRef = campaignFiltersRef.value?.$refs.dropdown as BDropdown;
  dropdownRef.hide(true);
}

function handleClearAllFilters() {
  filterState.filterSession = {
    status: [],
    managed: [],
    archived: [],
    tags: [],
  };
  defaultArchivedStatus.value = null;
  defaultManagedStatus.value = null;
}

function handleOnFilterClose() {
  const savedFilters = cloneDeep(filterState.savedFilters);
  filterState.filterSession.status = savedFilters.status ?? [];
  filterState.filterSession.managed = savedFilters.managed ?? [];
  filterState.filterSession.archived = savedFilters.archived ?? [];
  filterState.filterSession.tags = savedFilters.tags ?? [];
}

function capitalizeFirstLetter(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

return { __sfc: true,props, emit, campaignFiltersRef, searchTag, searchStatus, searchCampaigns, defaultManagedStatus, defaultArchivedStatus, campaignsStore, filterState, getAllTags, appliedFiltersLength, filteredTagValues, isFilterStateEmpty, handleTagSelection, isTagSelected, statusValues, isTagsSearchBarShown, isStatusSearchBarShown, handleAllValueSelected, statusSelection, handleStatusSelection, manageStatusSelection, handleOnFilterValues, handleClearAllFilters, handleOnFilterClose, capitalizeFirstLetter, Checkbox, DropdownTabContent, FilterDropdown, OptimizerIcon }
}

})